import React, { useState } from 'react';
import { useStore } from '../states/store';
import { soundLibrary } from '../utils/sfx/sound';
import { Stepper, Step, StepLabel, Box, Typography, Paper, Fade, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import g1 from '../assets/images/instructions/ssv2/i1.png';
import g2 from '../assets/images/instructions/ssv2/i2.png';
import g3 from '../assets/images/instructions/ssv2/i3.png';
import g4 from '../assets/images/instructions/ssv2/i4.png';
import g5 from '../assets/images/instructions/ssv2/i4.png';
import g6 from '../assets/images/instructions/ssv2/i5.png';
import g7 from '../assets/images/instructions/ssv2/i6.png';
import g9 from '../assets/images/instructions/ssv2/i7.png';

function SettingsEtc() {
    const { isSoundOn, toggleMute } = useStore();
    const [activeStep, setActiveStep] = useState(0);
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const steps = [
        { label: 'Placing Bet', icon: '💰', title: 'Placing Your Bet', desc: 'Choose your bet type and amount', color: 'from-purple-400 to-indigo-600', shadow: 'shadow-purple-500/20' },
        { label: 'Draw Results', icon: '🎲', title: 'Draw Results', desc: 'Watch the live draw or check results instantly', color: 'from-indigo-500 to-purple-600', shadow: 'shadow-indigo-500/20' },
        { label: 'Bet Results', icon: '✅', title: 'Check Your Results', desc: 'See how many numbers you matched', color: 'from-emerald-400 to-teal-600', shadow: 'shadow-emerald-500/20' },
        { label: 'Payout', icon: '💸', title: 'Collect Your Winnings', desc: 'Automatic payout to your account', color: 'from-fuchsia-500 to-purple-600', shadow: 'shadow-fuchsia-500/20' }
    ];

    const handleStepClick = (step) => {
        setActiveStep(step);
    };

    const handleNext = () => {
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const getStepIcon = (index) => {
        const step = steps[index];
        const isActive = index === activeStep;
        const isCompleted = index < activeStep;

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 36, sm: 40, md: 44 },
                    height: { xs: 36, sm: 40, md: 44 },
                    fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' },
                    opacity: isActive || isCompleted ? 1 : 0.4,
                    transform: isActive ? 'scale(1.15)' : 'scale(1)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: isActive ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                    borderRadius: '12px',
                    boxShadow: isActive ? '0 0 20px rgba(139, 92, 246, 0.3)' : 'none',
                    border: isActive ? '1px solid rgba(139, 92, 246, 0.4)' : '1px solid transparent'
                }}
            >
                {step.icon}
            </Box>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="modal-box flex flex-col gap-4 sm:gap-6 p-4 sm:p-6 md:p-8"
            style={{
                maxWidth: '1000px',
                width: '98% sm:95%',
                background: 'linear-gradient(135deg, #0a0a1a 0%, #0f0f1f 50%, #12122a 100%)',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                borderRadius: isMobile ? '16px' : '24px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(139, 92, 246, 0.25)',
                position: 'relative'
            }}
        >
            {/* Animated purple glow background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-600/20 rounded-full blur-[100px] animate-pulse" />
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-600/20 rounded-full blur-[100px] animate-pulse delay-1000" />
            </div>

            {/* Header & Settings */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 pb-4 sm:pb-6 border-b relative z-10" style={{ borderColor: 'rgba(139, 92, 246, 0.2)' }}>
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold" style={{
                        background: 'linear-gradient(135deg, #c4b5fd, #a78bfa)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        Settings & Tutorial
                    </h2>
                    <p className="text-sm mt-1" style={{ color: '#6b6b8d' }}>Configure your experience and learn how to play</p>
                </div>

                <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl backdrop-blur-sm transition-all w-full sm:w-auto justify-between sm:justify-start" style={{
                    background: 'rgba(26, 21, 53, 0.5)',
                    border: '1px solid rgba(139, 92, 246, 0.2)'
                }}>
                    <span className="font-semibold text-sm sm:text-base" style={{ color: '#c4b5fd' }}>Sound</span>
                    <label className="swap swap-rotate cursor-pointer">
                        <input type="checkbox" checked={isSoundOn} onChange={() => {
                            toggleMute()
                            if (!isSoundOn) soundLibrary.volumeToggle.play()
                        }} />
                        <svg className="swap-on fill-current drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]" style={{ color: '#a78bfa' }} xmlns="http://www.w3.org/2000/svg" width={isMobile ? "28" : "32"} height={isMobile ? "28" : "32"} viewBox="0 0 24 24">
                            <path d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z" />
                        </svg>
                        <svg className="swap-off fill-current" style={{ color: '#6b6b8d' }} xmlns="http://www.w3.org/2000/svg" width={isMobile ? "28" : "32"} height={isMobile ? "28" : "32"} viewBox="0 0 24 24">
                            <path d="M3,9H7L12,4V20L7,15H3V9M16.59,12L14,9.41L15.41,8L18,10.59L20.59,8L22,9.41L19.41,12L22,14.59L20.59,16L18,13.41L15.41,16L14,14.59L16.59,12Z" />
                        </svg>
                    </label>
                </div>
            </div>

            {/* Tutorial Section */}
            <div className="flex flex-col gap-4 sm:gap-6 relative z-10">

                {/* Stepper Navigation */}
                <Box sx={{ width: '100%', px: { xs: 0, sm: 2, md: 4 }, py: { xs: 1, sm: 2 } }}>
                    <Stepper
                        activeStep={activeStep}
                        alternativeLabel
                        sx={{
                            '& .MuiStepConnector-root': {
                                top: { xs: 18, sm: 20, md: 22 },
                                left: 'calc(-50% + 22px)',
                                right: 'calc(50% + 22px)',
                            },
                            '& .MuiStepConnector-line': {
                                borderColor: 'rgba(139, 92, 246, 0.2)',
                                borderTopWidth: 2,
                                transition: 'border-color 0.3s ease',
                            },
                            '& .Mui-active .MuiStepConnector-line, & .Mui-completed .MuiStepConnector-line': {
                                borderColor: '#8b5cf6',
                            },
                            '& .MuiStepLabel-label': {
                                color: '#6b6b8d',
                                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                                fontWeight: 500,
                                mt: 1,
                                fontFamily: '"Inter", sans-serif',
                                transition: 'all 0.3s',
                                display: { xs: 'none', sm: 'block' },
                            },
                            '& .MuiStepLabel-label.Mui-completed': {
                                color: '#a78bfa',
                            },
                            '& .MuiStepLabel-label.Mui-active': {
                                color: '#c4b5fd',
                                fontWeight: 600,
                            },
                        }}
                    >
                        {steps.map((step, index) => (
                            <Step
                                key={step.label}
                                onClick={() => handleStepClick(index)}
                                sx={{
                                    cursor: 'pointer',
                                    '& .MuiStepLabel-root': { cursor: 'pointer' },
                                }}
                            >
                                <StepLabel icon={getStepIcon(index)}>
                                    {step.label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Instructions Panel */}
                <Fade in={true} key={activeStep} timeout={500}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, sm: 3, md: 5 },
                            bgcolor: 'rgba(26, 21, 53, 0.4)',
                            borderRadius: { xs: '16px', sm: '20px', md: '24px' },
                            border: '1px solid rgba(139, 92, 246, 0.2)',
                            backdropFilter: 'blur(10px)',
                            minHeight: { xs: '500px', sm: '600px', md: '650px' },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Decorative Background Glow */}
                        <div className={`absolute -top-32 -right-32 w-96 h-96 bg-gradient-to-br ${steps[activeStep].color} rounded-full blur-[120px] opacity-10 pointer-events-none transition-all duration-700 hidden sm:block`} />

                        <div className="flex flex-col gap-4 sm:gap-6 relative z-10 w-full">
                            {/* Step Header */}
                            <div className="flex flex-col gap-1 pb-3 sm:pb-5" style={{ borderBottom: '1px solid rgba(139, 92, 246, 0.15)' }}>
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <div className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br ${steps[activeStep].color} ${steps[activeStep].shadow} shadow-lg`}>
                                        <span className="text-xl sm:text-2xl leading-none block filter drop-shadow-md">{steps[activeStep].icon}</span>
                                    </div>
                                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
                                        color: '#c4b5fd', 
                                        fontWeight: 700, 
                                        fontFamily: '"Inter", sans-serif', 
                                        letterSpacing: '-0.5px',
                                        fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                                    }}>
                                        {steps[activeStep].title}
                                    </Typography>
                                </div>
                                <Typography variant="body2" sx={{ 
                                    color: '#6b6b8d', 
                                    ml: { xs: '50px', sm: '60px' },
                                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' }
                                }}>
                                    {steps[activeStep].desc}
                                </Typography>
                            </div>

                            {/* Step 0: Placing Bet */}
                            {activeStep === 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className="w-full"
                                    style={{ color: '#c4b5fd' }}
                                >
                                    <h4 className="font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base" style={{ color: '#a78bfa' }}>
                                        <span>❖</span> Instructions Overview
                                    </h4>

                                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                                        {[
                                            { num: 1, title: 'Choose Bet Type', desc: 'Select whether the final price will go "Up" or "Down" from the starting point.', img: g1 },
                                            { num: 2, title: 'Select Amount', desc: 'Use quick buttons or enter a custom amount. Min: 1만 • Max: 100만', img: g2 },
                                            { num: 3, title: 'Confirm & Place', desc: 'Review your choices and click "Place Bet" to lock in your ticket before time runs out.', img: g3 }
                                        ].map((item, idx) => (
                                            <motion.div
                                                key={item.num}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                                                className="p-3 sm:p-4 rounded-xl sm:rounded-2xl flex flex-col lg:flex-row gap-3 sm:gap-4 items-start lg:items-center transition-colors"
                                                style={{
                                                    background: 'rgba(26, 21, 53, 0.4)',
                                                    border: '1px solid rgba(139, 92, 246, 0.15)'
                                                }}
                                            >
                                                <div className="flex-1">
                                                    <h5 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base" style={{ color: '#f3f0ff' }}>{item.num}. {item.title}</h5>
                                                    <p className="text-xs sm:text-sm" style={{ color: '#6b6b8d' }}>{item.desc}</p>
                                                </div>
                                                <div className="shrink-0 rounded-lg sm:rounded-xl overflow-hidden border w-full lg:w-auto" style={{ borderColor: 'rgba(139, 92, 246, 0.2)' }}>
                                                    <img src={item.img} alt={`Step ${item.num}`} className="h-32 sm:h-36 md:h-40 w-full lg:w-72 object-cover" />
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>

                                    <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl mt-4 sm:mt-6 flex items-start gap-2 sm:gap-3" style={{
                                        background: 'rgba(139, 92, 246, 0.1)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)'
                                    }}>
                                        <span className="text-lg sm:text-xl">💡</span>
                                        <div>
                                            <p className="font-semibold text-xs sm:text-sm" style={{ color: '#c4b5fd' }}>Pro tip</p>
                                            <p className="text-xs sm:text-sm mt-0.5" style={{ color: '#a78bfa' }}>You can play multiple tickets simultaneously for better chances and layered strategies!</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 1: Draw Results */}
                            {activeStep === 1 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start"
                                >
                                    <div className="flex flex-col gap-4 sm:gap-6">
                                        <div className="p-4 sm:p-5 rounded-xl sm:rounded-2xl" style={{
                                            background: 'rgba(26, 21, 53, 0.4)',
                                            border: '1px solid rgba(139, 92, 246, 0.15)'
                                        }}>
                                            <h5 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg flex items-center gap-2" style={{ color: '#c4b5fd' }}>
                                                <span style={{ color: '#a78bfa' }}>
                                                   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4">
                                                        <path fillRule="evenodd" d="M1 8a7 7 0 1 1 14 0A7 7 0 0 1 1 8Zm7.75-4.25a.75.75 0 0 0-1.5 0V8c0 .414.336.75.75.75h3.25a.75.75 0 0 0 0-1.5h-2.5v-3.5Z" clipRule="evenodd" />
                                                    </svg>
 
                                                </span> Draw Sequence
                                            </h5>
                                            <ul className="space-y-2 sm:space-y-3">
                                                <li className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm">
                                                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{
                                                        background: 'rgba(139, 92, 246, 0.2)',
                                                        color: '#c4b5fd'
                                                    }}>1</div>
                                                    <span className="pt-0.5" style={{ color: '#6b6b8d' }}>The <strong style={{ color: '#c4b5fd' }}>Starting Time</strong> and <strong style={{ color: '#c4b5fd' }}>Starting Price</strong> are captured.</span>
                                                </li>
                                                <li className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm">
                                                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{
                                                        background: 'rgba(139, 92, 246, 0.2)',
                                                        color: '#c4b5fd'
                                                    }}>2</div>
                                                    <span className="pt-0.5" style={{ color: '#6b6b8d' }}>The chart animation begins charting the real-time live data for the duration of the round.</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-3 sm:gap-4">
                                        <div className="rounded-lg sm:rounded-xl overflow-hidden border shadow-xl relative group" style={{ borderColor: 'rgba(139, 92, 246, 0.2)' }}>
                                            <img src={g5} alt="Starting Time" className="w-full h-auto object-cover" />
                                        </div>
                                        <div className="rounded-lg sm:rounded-xl overflow-hidden border shadow-xl relative group" style={{ borderColor: 'rgba(139, 92, 246, 0.2)' }}>
                                            <img src={g6} alt="Live Draw" className="w-full h-auto object-cover" />
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 2: Bet Results */}
                            {activeStep === 2 && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5 }}
                                    className="w-full h-full flex flex-col items-center justify-center gap-4 sm:gap-8 py-2 sm:py-4"
                                >
                                    <div className="text-center max-w-lg px-2 sm:px-4">
                                        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full mb-3 sm:mb-4" style={{
                                            background: 'rgba(139, 92, 246, 0.2)',
                                            color: '#a78bfa'
                                        }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2" style={{ color: '#c4b5fd' }}>Instant Verification</h3>
                                        <p className="text-xs sm:text-sm" style={{ color: '#6b6b8d' }}>At the end of the round, the final price is stamped. Your bets are instantly evaluated against the result.</p>
                                    </div>

                                    <div className="w-full max-w-2xl rounded-xl sm:rounded-2xl overflow-hidden border shadow-[0_20px_50px_rgba(0,0,0,0.5)] transform hover:scale-[1.02] transition-transform duration-500" style={{ borderColor: 'rgba(139, 92, 246, 0.2)' }}>
                                        <img src={g7} alt="Bet Results" className="w-full h-auto object-cover" />
                                        <div className="p-2 sm:p-4 border-t flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2" style={{
                                            background: 'rgba(26, 21, 53, 0.6)',
                                            borderColor: 'rgba(139, 92, 246, 0.15)'
                                        }}>
                                            <span className="text-xs sm:text-sm font-medium" style={{ color: '#c4b5fd' }}>Detailed result slips generated</span>
                                            <span className="text-xs px-2 py-1 rounded-md" style={{
                                                background: 'rgba(139, 92, 246, 0.2)',
                                                color: '#a78bfa'
                                            }}>Automated Systems</span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 3: Payout */}
                            {activeStep === 3 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className="w-full grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-8 items-center py-2 sm:py-4"
                                >
                                    <div className="lg:col-span-2 flex flex-col gap-4 sm:gap-6">
                                        <div className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl shadow-xl relative overflow-hidden" style={{
                                            background: 'linear-gradient(135deg, rgba(26, 21, 53, 0.8), rgba(18, 18, 42, 0.8))',
                                            border: '1px solid rgba(139, 92, 246, 0.2)'
                                        }}>
                                            <div className="absolute top-0 right-0 w-20 h-20 sm:w-32 sm:h-32 rounded-full blur-[40px]" style={{ background: 'rgba(139, 92, 246, 0.1)' }} />

                                            <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2" style={{ color: '#c4b5fd' }}>
                                                <span style={{ color: '#a78bfa' }}>🏆</span> Prize Breakdown
                                            </h4>

                                            <p className="text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6" style={{ color: '#6b6b8d' }}>
                                                Winning bets automatically trigger payouts directly to your account balance. No manual claims required!
                                            </p>

                                            <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl flex items-start gap-2 sm:gap-3" style={{
                                                background: 'rgba(139, 92, 246, 0.1)',
                                                border: '1px solid rgba(139, 92, 246, 0.2)'
                                            }}>
                                                <span className="text-lg sm:text-xl animate-bounce mt-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="text-amber-300 size-5">
                                                        <path fillRule="evenodd" d="M5 4a.75.75 0 0 1 .738.616l.252 1.388A1.25 1.25 0 0 0 6.996 7.01l1.388.252a.75.75 0 0 1 0 1.476l-1.388.252A1.25 1.25 0 0 0 5.99 9.996l-.252 1.388a.75.75 0 0 1-1.476 0L4.01 9.996A1.25 1.25 0 0 0 3.004 8.99l-1.388-.252a.75.75 0 0 1 0-1.476l1.388-.252A1.25 1.25 0 0 0 4.01 6.004l.252-1.388A.75.75 0 0 1 5 4ZM12 1a.75.75 0 0 1 .721.544l.195.682c.118.415.443.74.858.858l.682.195a.75.75 0 0 1 0 1.442l-.682.195a1.25 1.25 0 0 0-.858.858l-.195.682a.75.75 0 0 1-1.442 0l-.195-.682a1.25 1.25 0 0 0-.858-.858l-.682-.195a.75.75 0 0 1 0-1.442l.682-.195a1.25 1.25 0 0 0 .858-.858l.195-.682A.75.75 0 0 1 12 1ZM10 11a.75.75 0 0 1 .728.568.968.968 0 0 0 .704.704.75.75 0 0 1 0 1.456.968.968 0 0 0-.704.704.75.75 0 0 1-1.456 0 .968.968 0 0 0-.704-.704.75.75 0 0 1 0-1.456.968.968 0 0 0 .704-.704A.75.75 0 0 1 10 11Z" clipRule="evenodd" />
                                                    </svg>


                                                </span>
                                                <div>
                                                    <p className="font-semibold text-xs sm:text-sm" style={{ color: '#c4b5fd' }}>Instant Credit</p>
                                                    <p className="text-xs sm:text-sm mt-0.5 sm:mt-1" style={{ color: '#a78bfa' }}>Winnings are available for your next bet immediately.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="lg:col-span-3">
                                        <div className="rounded-xl sm:rounded-2xl overflow-hidden border-2 shadow-[0_0_40px_rgba(139,92,246,0.15)] relative group" style={{ borderColor: 'rgba(139, 92, 246, 0.3)' }}>
                                            <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/10 to-transparent pointer-events-none" />
                                            <img src={g9} alt="Payout Overview" className="w-full h-auto object-cover scale-[1.01]" />
                                        </div>
                                    </div>
                                    
                                    <div className="lg:col-span-5 p-3 sm:p-4 rounded-lg sm:rounded-xl flex items-start gap-2 sm:gap-3" style={{
                                        background: 'rgba(139, 92, 246, 0.1)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)'
                                    }}>
                                        <span className="text-lg sm:text-xl mt-1 animate-spin">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="text-amber-300 size-5">
                                                <path fillRule="evenodd" d="M13.836 2.477a.75.75 0 0 1 .75.75v3.182a.75.75 0 0 1-.75.75h-3.182a.75.75 0 0 1 0-1.5h1.37l-.84-.841a4.5 4.5 0 0 0-7.08.932.75.75 0 0 1-1.3-.75 6 6 0 0 1 9.44-1.242l.842.84V3.227a.75.75 0 0 1 .75-.75Zm-.911 7.5A.75.75 0 0 1 13.199 11a6 6 0 0 1-9.44 1.241l-.84-.84v1.371a.75.75 0 0 1-1.5 0V9.591a.75.75 0 0 1 .75-.75H5.35a.75.75 0 0 1 0 1.5H3.98l.841.841a4.5 4.5 0 0 0 7.08-.932.75.75 0 0 1 1.025-.273Z" clipRule="evenodd" />
                                            </svg>

                                        </span>
                                        <div>
                                            <p className="font-semibold text-xs sm:text-sm" style={{ color: '#c4b5fd' }}>Result : Draw</p>
                                            <p className="text-xs sm:text-sm mt-0.5 sm:mt-1" style={{ color: '#a78bfa' }}>All current bets resulting to 'DRAW' will be refunded during payout.</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 mt-4 sm:mt-8 pt-4 sm:pt-6 relative z-10 w-full" style={{ borderTop: '1px solid rgba(139, 92, 246, 0.15)' }}>
                            <button
                                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed font-medium text-xs sm:text-sm flex items-center justify-center gap-2 order-2 sm:order-1"
                                style={{
                                    border: '1px solid rgba(139, 92, 246, 0.3)',
                                    background: 'rgba(26, 21, 53, 0.6)',
                                    color: '#c4b5fd'
                                }}
                                onClick={handleBack}
                                disabled={activeStep === 0}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(26, 21, 53, 0.6)';
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                                Previous
                            </button>

                            <div className="flex gap-1 sm:gap-2 order-1 sm:order-2">
                                {steps.map((_, i) => (
                                    <div
                                        key={i}
                                        className={`h-1 sm:h-1.5 rounded-full transition-all duration-300 ${
                                            i === activeStep ? 'w-4 sm:w-6' : i < activeStep ? 'w-1.5 sm:w-2' : 'w-1.5 sm:w-2'
                                        }`}
                                        style={{
                                            background: i === activeStep ? '#8b5cf6' : i < activeStep ? '#a78bfa' : 'rgba(139, 92, 246, 0.3)'
                                        }}
                                    />
                                ))}
                            </div>

                            <button
                                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl shadow-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed font-medium text-xs sm:text-sm flex items-center justify-center gap-2 order-3"
                                style={{
                                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                    color: '#ffffff',
                                    boxShadow: '0 0 15px rgba(139, 92, 246, 0.3)'
                                }}
                                onClick={handleNext}
                                disabled={activeStep === steps.length - 1}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, #9b6ef6, #8b4aed)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
                                }}
                            >
                                Next
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </Paper>
                </Fade>
            </div>
        </motion.div>
    );
}

export default SettingsEtc;